import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

// ---------------------------------------------------------------------------
// Study Design Suite cube logo — shared across all modules.
// ---------------------------------------------------------------------------

const SVG_OUTER =
    `<svg width="98" height="128" viewBox="0 0 98 128" fill="none" xmlns="http://www.w3.org/2000/svg">` +
    `<path d="M49 40.0918V14.0918L0 43.0918L22.5 55.5918L49 40.0918Z" fill="#0E1F55"/>` +
    `<path d="M75.5001 55.5926L98 42.5927L49.0001 14.0926L49.0001 40.0926L75.5001 55.5926Z" fill="#C1DFFB"/>` +
    `<path d="M75.5 55.5918L98 42.5918V99.0918L75.5 86.0918V55.5918Z" fill="#65615D"/>` +
    `<path d="M48.5 127.092L98 99.0918L75.5 86.0918L48.5 101.092V127.092Z" fill="#C4B8AC"/>` +
    `<path d="M0 99.0918L48.5 127.092V101.092L22.5 86.0918L0 99.0918Z" fill="#3676EB"/>` +
    `<path d="M22.5 55.5918V86.0918L0 99.0918V43.0918L22.5 55.5918Z" fill="#1B38BA"/>`;

// ---------------------------------------------------------------------------
// Module-specific overlay icons — exact SVG paths from design assets.
// ---------------------------------------------------------------------------

/** SOA: Gold table-grid icon */
const SOA_INNER = `<path d="M64.4445 52H32.5555C30.05 52 28 53.85 28 56.1111V84.8889C28 87.15 30.05 89 32.5555 89H64.4445C66.95 89 69 87.15 69 84.8889V56.1111C69 53.85 66.95 52 64.4445 52ZM64.4445 56.1111V62.2778H43.9445V56.1111H64.4445ZM39.3889 56.1111V62.2778H32.5555V56.1111H39.3889ZM32.5555 84.8889V66.3889H39.3889V84.8889H32.5555ZM43.9445 84.8889V66.3889H64.4445V84.8889H43.9445Z" fill="#F0B70D"/>`;

/** ECB: Teal people-criteria icon */
const ECB_INNER = `<path fill-rule="evenodd" clip-rule="evenodd" d="M40.9416 72.2309H40.6656C38.5987 72.1675 36.635 71.2505 35.1858 69.672C33.7366 68.0935 32.9145 65.9761 32.8916 63.7633V63.4679C32.9006 62.3463 33.116 61.2375 33.5254 60.2048C33.9349 59.1722 34.5304 58.2361 35.2778 57.4497C36.0254 56.6634 36.9103 56.0424 37.882 55.6222C38.8539 55.2019 39.8935 54.9905 40.9416 55.0003H41.2176C43.2863 55.0577 45.2533 55.9728 46.7037 57.5525C48.1541 59.1323 48.9744 61.2532 48.9916 63.4679V64.0341C48.9024 66.2499 48.0135 68.3423 46.5121 69.871C45.0108 71.3998 43.0138 72.2457 40.9416 72.2309ZM37.7627 62.0806C37.5865 62.519 37.4944 62.9908 37.4916 63.4679V63.7633C37.5142 64.6873 37.8642 65.5675 38.4707 66.2257C39.077 66.8839 39.8946 67.2708 40.7576 67.3078H40.9416C41.8064 67.3234 42.6444 66.9861 43.2852 66.3643C43.926 65.7424 44.3216 64.8829 44.3916 63.9603V63.4679C44.3689 62.544 44.019 61.6636 43.4125 61.0055C42.8061 60.3473 41.9885 59.9605 41.1256 59.9233H40.9416C40.496 59.9069 40.0518 59.9863 39.6353 60.1567C39.2188 60.3271 38.8385 60.5852 38.5169 60.9157C38.1953 61.2461 37.9388 61.6422 37.7627 62.0806ZM56.7656 72.2309C54.9937 72.1572 53.2948 71.4559 51.9356 70.2371C52.9517 68.3447 53.5202 66.2152 53.5916 64.0341V63.4679C53.58 61.1795 53.0176 58.933 51.9586 56.945C53.3924 55.6882 55.1883 55.001 57.0416 55.0003H57.3176C59.3864 55.0577 61.3534 55.9727 62.8037 57.5525C64.2541 59.1323 65.0744 61.2532 65.0916 63.4679V64.0341C65.0023 66.2499 64.1135 68.3422 62.6121 69.8709C61.1108 71.3997 59.1139 72.2457 57.0416 72.2309H56.7656ZM67.077 78.167C68.0247 78.8042 68.7932 79.7046 69.3006 80.7723L71.9916 87H60.9516L57.2716 78.4831C57.0361 78.0127 56.7588 77.5676 56.4436 77.1539H63.9186C65.0376 77.1796 66.1291 77.5298 67.077 78.167ZM47.8186 77.1539H34.2256C33.0991 77.1428 31.9932 77.4778 31.0392 78.1192C30.0852 78.7606 29.3232 79.6814 28.8436 80.7723L25.9916 87H55.8916L53.2006 80.7723C52.6933 79.7046 51.9247 78.8042 50.9769 78.167C50.0292 77.5298 48.9376 77.1797 47.8186 77.1539Z" fill="#32A6AE"/>`;

const SVG_CLOSE = `</svg>`;

// ---------------------------------------------------------------------------
// Tab titles per module.
// ---------------------------------------------------------------------------

const TITLE_MAP: Record<string, string> = {
    soa: 'Schedule of Activities',
    ecb: 'Eligibility Criteria',
};

// ---------------------------------------------------------------------------

type ModuleType = 'soa' | 'ecb';

interface ModuleFavicons {
    loading: string;
    loaded: string;
}

@Injectable({ providedIn: 'root' })
export class FaviconService {
    private readonly favicons: Record<ModuleType, ModuleFavicons> = {
        soa: {
            loading:
                'data:image/svg+xml,' +
                encodeURIComponent(SVG_OUTER + SVG_CLOSE),
            loaded:
                'data:image/svg+xml,' +
                encodeURIComponent(SVG_OUTER + SOA_INNER + SVG_CLOSE),
        },
        ecb: {
            loading:
                'data:image/svg+xml,' +
                encodeURIComponent(SVG_OUTER + SVG_CLOSE),
            loaded:
                'data:image/svg+xml,' +
                encodeURIComponent(SVG_OUTER + ECB_INNER + SVG_CLOSE),
        },
    };

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly router: Router
    ) {}

    /**
     * Subscribe to Angular Router NavigationEnd events so that the favicon
     * and title are applied as soon as the router reaches the SOA/ECB route.
     */
    watchRouterAndApply(): void {
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe((event) => {
                const module = this.detectModuleFromUrl(
                    event.urlAfterRedirects || event.url
                );
                if (module) {
                    this.applyForModule(module);
                }
            });
    }

    /**
     * Register a beforeunload handler that resets the favicon to the
     * loading state (cube only, no overlay). The browser snapshots this
     * into its internal favicon cache, so on refresh the cached icon
     * is the plain cube — not the module-specific one.
     */
    registerResetOnLeave(): void {
        window.addEventListener('beforeunload', () => {
            const module = this.detectModuleFromUrl(globalThis.location.href);
            if (module) {
                this.setFavicon(this.favicons[module].loading);
                this.document.title = TITLE_MAP[module];
            }
        });
    }

    private applyForModule(module: ModuleType): void {
        this.setFavicon(this.favicons[module].loaded);
        this.document.title = TITLE_MAP[module];
    }

    private detectModuleFromUrl(url: string): ModuleType | null {
        if (url.includes('/screening-of-activities/')) {
            return 'soa';
        }
        if (url.includes('/ecb-workbench/')) {
            return 'ecb';
        }
        return null;
    }

    private setFavicon(href: string): void {
        const link = this.document.querySelector(
            "link[rel='icon']"
        ) as HTMLLinkElement;
        if (link) {
            link.href = href;
        }
    }
}
